import { ArrowLeftIcon, PlusIcon, SyncIcon } from "@primer/octicons-react";
import { Box, Checkbox, FormControl } from "@primer/react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { hocSinhApi } from "../../api/hocSinhApi";
import { meetingApi } from "../../api/meetingApi";
import { meetingBusyApi } from "../../api/meetingBusyApi";
import { meetingPickApi } from "../../api/meetingPickApi";
import ComboboxLop from "../../components/combobox-lop";
import Button from "../../components/ui/button";
import DateInput from "../../components/ui/date-input";
import Text from "../../components/ui/text";
import TextInput from "../../components/ui/text-input";
import { NotifyHelper } from "../../helpers/toast";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { IMeetingSlot, SlotStatus } from "../../models/response/meeting/IMeetingSlot";
import { IMeetingVm } from "../../models/response/meeting/IMeetingVm";
import { IMeetingBusy } from "../../models/response/meeting/IMeetingBusy";
import { IMeetingPick } from "../../models/response/meeting/IMeetingPick";
import MeetingSchedule from "./MeetingSchedule";
import SelectSubjectModal from "./SelectSubjectModal";
import { pl_tkb_chitiet_view } from "../../models/response/thoi-khoa-bieu/pl_tkb_chitiet";
import AssignTeacherGrid from "./AssignTeacherGrid";

type Subject = {
    id: number;
    name: string;
    sis_giaovien_id: number;
};

const MeetingDetail = () => {
    const { id: pId }: any = useParams();
    const id: number = parseInt(pId);
    const navigate = useNavigate();
    const { nam_hoc, dm_truong_id, hoc_ky } = useCommonSelectedHook();
    const [hocSinhs, setHocSinhs] = useState<any[]>([]);
    const [meeting, setMeeting] = useState<IMeetingVm>({
        id: 0,
        scopes: [],
        slots: [],
        assigns: []
    } as any);
    const [htmlDescription, setHtmlDescription] = useState("");
    const [homeroomTeacherId, setHomeroomTeacherId] = useState<number>(0);
    const [homeroomTeacherName, setHomeroomTeacherName] = useState("");
    const [openSubjectModal, setOpenSubjectModal] = useState(false);
    const [subjectOptions, setSubjectOptions] = useState<
        { id: number; name: string; sis_giaovien_id: number }[]
    >([]);

    const [selectedSubjects, setSelectedSubjects] = useState<
        { id: number; name: string; sis_giaovien_id: number }[]
    >([]);

    const { register, handleSubmit, reset, clearErrors, setError, formState: { errors }, control } = useForm<IMeetingVm>({
        mode: "all"
    })

    const [scheduleConfig, setScheduleConfig] = useState({
        morningStart: "08:00",
        morningEnd: "11:30",
        afternoonStart: "13:30",
        afternoonEnd: "17:00",
        duration: 15,
        breakTime: 5
    });

    const [prevConfig, setPrevConfig] = useState(scheduleConfig);
    const isConfigChanged =
        prevConfig.duration !== scheduleConfig.duration ||
        prevConfig.breakTime !== scheduleConfig.breakTime ||
        prevConfig.morningStart !== scheduleConfig.morningStart ||
        prevConfig.morningEnd !== scheduleConfig.morningEnd ||
        prevConfig.afternoonStart !== scheduleConfig.afternoonStart ||
        prevConfig.afternoonEnd !== scheduleConfig.afternoonEnd;

    const lopIdsSelected = useMemo(() => {
        return meeting?.scopes?.map(x => x.dm_lop_id) ?? [];
    }, [meeting])

    useEffect(() => {
        handleGetDataAsync();
    }, [id])

    const handleGetDataAsync = async () => {
        const res = await meetingApi.selectDetail(id);
        if (res.is_success) {
            if (!res.data) return;
            setMeeting({
                ...res.data,
                assigns: res.data.assigns ?? [],
                slots: (res.data.slots ?? []).map((s: IMeetingSlot) => {
                    let date = s.start_at;

                    if (date && !date.endsWith("Z")) {
                        date = date + "Z";
                    }

                    const d = new Date(date);

                    return {
                        ...s,
                        start_at: isNaN(d.getTime()) ? null : d.toISOString()
                    };
                }).sort((a: IMeetingSlot, b: IMeetingSlot) =>
                    new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
                )
            });

            const firstSlot = res.data?.slots?.find((x: IMeetingSlot) => !x.sis_lop_monhoc_id)
                ?? res.data?.slots?.[0];

            if (firstSlot) {
                setHomeroomTeacherId(firstSlot.sis_giaovien_id);
                setHomeroomTeacherName(firstSlot.sis_giaovien_name);

                const toHHmm = (d: Date) =>
                    `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

                // Normalize + filter slots of homeroom teacher to detect config
                const homeroomNorm: IMeetingSlot[] = (res.data?.slots ?? [])
                    .filter((x: IMeetingSlot) => x.sis_giaovien_id === firstSlot.sis_giaovien_id)
                    .map((s: IMeetingSlot) => {
                        let date = s.start_at;
                        if (date && !date.endsWith("Z")) date = date + "Z";
                        const d = new Date(date);
                        return { ...s, start_at: isNaN(d.getTime()) ? s.start_at : d.toISOString() };
                    })
                    .sort((a: IMeetingSlot, b: IMeetingSlot) =>
                        new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
                    );

                // Only use slots from the first day to avoid cross-day noise
                const firstDay = homeroomNorm[0]
                    ? new Date(homeroomNorm[0].start_at).toDateString()
                    : null;
                const oneDaySlots = firstDay
                    ? homeroomNorm.filter(s => new Date(s.start_at).toDateString() === firstDay)
                    : homeroomNorm;

                const morningSlots = oneDaySlots.filter(s => new Date(s.start_at).getHours() < 12);
                const afternoonSlots = oneDaySlots.filter(s => new Date(s.start_at).getHours() >= 12);

                // Calculate start-to-start interval between consecutive same-session slots
                let slotInterval = 0;
                for (let i = 0; i < oneDaySlots.length - 1; i++) {
                    const interval = (new Date(oneDaySlots[i + 1].start_at).getTime() - new Date(oneDaySlots[i].start_at).getTime()) / 60000;
                    if (interval > 0 && interval < 120) {
                        slotInterval = interval;
                        break;
                    }
                }

                // duration_in_minute may not be returned by API — use it if valid, else derive from interval
                const rawDuration = firstSlot.duration_in_minute;
                const detectedBreakTime = (rawDuration && rawDuration > 0 && slotInterval > rawDuration)
                    ? slotInterval - rawDuration
                    : 5;
                const detectedDuration = (rawDuration && rawDuration > 0)
                    ? rawDuration
                    : (slotInterval > detectedBreakTime ? slotInterval - detectedBreakTime : slotInterval || 15);

                const recovered = {
                    duration: detectedDuration,
                    breakTime: detectedBreakTime,
                    morningStart: "08:00",
                    morningEnd: "11:30",
                    afternoonStart: "13:30",
                    afternoonEnd: "17:00"
                };

                if (morningSlots.length > 0) {
                    recovered.morningStart = toHHmm(new Date(morningSlots[0].start_at));
                    const lastM = morningSlots[morningSlots.length - 1];
                    recovered.morningEnd = toHHmm(
                        new Date(new Date(lastM.start_at).getTime() + detectedDuration * 60000)
                    );
                }

                if (afternoonSlots.length > 0) {
                    recovered.afternoonStart = toHHmm(new Date(afternoonSlots[0].start_at));
                    const lastA = afternoonSlots[afternoonSlots.length - 1];
                    recovered.afternoonEnd = toHHmm(
                        new Date(new Date(lastA.start_at).getTime() + detectedDuration * 60000)
                    );
                }

                setScheduleConfig(recovered);
                setPrevConfig(recovered);
            }

            const uniqueSubjects: Subject[] = Array.from(
                new Map<number, Subject>(
                    (res.data?.slots ?? [])
                        .filter((x: IMeetingSlot) => x.sis_lop_monhoc_id > 0)
                        .map((x: IMeetingSlot) => [
                            x.sis_lop_monhoc_id,
                            {
                                id: x.sis_lop_monhoc_id,
                                name: x.sis_giaovien_name,
                                sis_giaovien_id: x.sis_giaovien_id
                            }
                        ])
                ).values()
            );
            setSelectedSubjects(uniqueSubjects);

            reset({
                ...res.data,
                registration_end_at: res.data?.registration_end_at ? moment(res.data?.registration_end_at).format("YYYY-MM-DD HH:mm") : undefined,
                registration_start_at: res.data?.registration_start_at ? moment(res.data?.registration_start_at).format("YYYY-MM-DD HH:mm") : undefined,
                event_time_start: res.data?.event_time_start ? moment(res.data?.event_time_start).format("YYYY-MM-DD") : undefined,
                event_time_end: res.data?.event_time_end ? moment(res.data?.event_time_end).format("YYYY-MM-DD") : undefined,
            })
            setHtmlDescription(res.data?.description ?? "")
        }
    }

    const handeGetHocSinhs = async () => {
        const res = await hocSinhApi.Select({
            dm_he_id: 0,
            dm_khoi_id: 0,
            dm_lop_id: lopIdsSelected[0],
            dm_truong_id,
            nam_hoc
        });
        if (res.is_success) {
            setHocSinhs(res.data);
        } else {
            NotifyHelper.Error(res.message ?? "Error")
        }
    }

    useEffect(() => {
        if (!lopIdsSelected.length || !lopIdsSelected[0]) return;

        handeGetHocSinhs();
    }, [lopIdsSelected[0]]);

    const formatDateTime = (val: string) => {
        if (!val) return null;
        return moment(val).format("YYYY-MM-DDTHH:mm:ss");
    };

    const onSubmit = async (data: any) => {
        let isValid = true;
        if ((meeting?.scopes ?? []).filter(x => x.dm_lop_id > 0).length <= 0) {
            isValid = false;
            setError("scopes", {});
        }
        if ((meeting?.slots ?? []).length <= 0) {
            isValid = false;
            setError("slots", {});
        }

        if (!isValid) return;

        const res = await meetingApi.saveChange({
            ...meeting,
            nam_hoc: nam_hoc,
            ...data,
            registration_start_at: formatDateTime(data.registration_start_at),
            registration_end_at: formatDateTime(data.registration_end_at),
            scopes: meeting.scopes,
            slots: meeting.slots,
            assigns: meeting.assigns,
            description: htmlDescription,
            is_published: meeting?.is_published ?? false,
            is_allow_re_submit: meeting?.is_allow_re_submit ?? false,
            is_homeroom_subject_meeting: meeting?.is_homeroom_subject_meeting ?? false,
            is_assign_teacher_enabled: meeting?.is_assign_teacher_enabled ?? false,
            id: id,
            dm_truong_id: dm_truong_id
        })
        if (res.is_success) {
            NotifyHelper.Success("Success");
            navigate(`../../meeting/${res.data}`, { replace: true });
        } else {
            NotifyHelper.Error(res.message ?? "Error");
        }


    }

    useEffect(() => {
        if (!meeting?.event_time_start || !meeting?.event_time_end) return;
        if (!meeting?.scopes?.length) return;
        if (!homeroomTeacherId && selectedSubjects.length === 0) return;

        const slots: IMeetingSlot[] = [];

        const addMinutes = (date: Date, minutes: number) =>
            new Date(date.getTime() + minutes * 60000);

        let tempId = -1;

        const buildSlots = (
            baseDate: Date,
            start: string,
            end: string,
            teacherId: number,
            teacherName: string,
            subjectId?: number
        ) => {
            let current = new Date(baseDate.getTime());

            const [sh, sm] = start.split(":");
            current.setHours(+sh, +sm, 0, 0);

            const endTime = new Date(baseDate.getTime());
            const [eh, em] = end.split(":");
            endTime.setHours(+eh, +em, 0, 0);

            while (current < endTime) {
                const next = addMinutes(current, scheduleConfig.duration);
                if (next > endTime) break;

                const existed = slots.some(s =>
                    s.sis_giaovien_id === teacherId &&
                    s.start_at === current.toISOString()
                );

                if (existed) {
                    current = addMinutes(next, scheduleConfig.breakTime);
                    continue;
                }

                slots.push({
                    id: tempId--,
                    sis_meeting_id: meeting.id,
                    start_at: current.toISOString(),
                    duration_in_minute: scheduleConfig.duration,
                    sis_giaovien_id: teacherId,
                    sis_giaovien_name: teacherName,
                    sis_lop_monhoc_id: subjectId ?? 0,
                    status: SlotStatus.Free
                });

                current = addMinutes(next, scheduleConfig.breakTime);
            }
        };

        const startDate = new Date(meeting.event_time_start);
        const endDate = new Date(meeting.event_time_end);

        let currentDate = new Date(startDate.getTime());

        while (currentDate <= endDate) {
            const baseDate = new Date(currentDate.getTime()); // ✅ clone ngày

            // GVCN
            buildSlots(
                baseDate,
                scheduleConfig.morningStart,
                scheduleConfig.morningEnd,
                homeroomTeacherId,
                homeroomTeacherName
            );

            buildSlots(
                baseDate,
                scheduleConfig.afternoonStart,
                scheduleConfig.afternoonEnd,
                homeroomTeacherId,
                homeroomTeacherName
            );

            // GV bộ môn
            selectedSubjects.forEach(gv => {
                buildSlots(
                    baseDate,
                    scheduleConfig.morningStart,
                    scheduleConfig.morningEnd,
                    gv.sis_giaovien_id,
                    gv.name,
                    gv.id
                );

                buildSlots(
                    baseDate,
                    scheduleConfig.afternoonStart,
                    scheduleConfig.afternoonEnd,
                    gv.sis_giaovien_id,
                    gv.name,
                    gv.id
                );
            });

            currentDate.setDate(currentDate.getDate() + 1);
        }

        setMeeting(prev => {
            if (isConfigChanged) {
                return {
                    ...prev,
                    slots: slots
                };
            }

            const existing = prev.slots ?? [];

            const newSlots = slots.filter(ns =>
                !existing.some(es =>
                    es.start_at === ns.start_at &&
                    es.sis_giaovien_id === ns.sis_giaovien_id
                )
            );

            return {
                ...prev,
                slots: [...existing, ...newSlots]
            };
        });

        setPrevConfig(scheduleConfig);

    }, [
        scheduleConfig,
        meeting?.event_time_start,
        meeting?.event_time_end,
        meeting?.scopes,
        homeroomTeacherId,
        selectedSubjects
    ]);


    const handleImportBusyFromTkb = async () => {
        try {
            const res = await meetingApi.getTkbByTeacher({
                tu_ngay: meeting.event_time_start,
                den_ngay: meeting.event_time_end,
                sis_giaovien_ids: [
                    homeroomTeacherId,
                    ...selectedSubjects.map(x => x.sis_giaovien_id)
                ],
                dm_lop_ids: lopIdsSelected,
                dm_phong_ids: [],
                dm_truong_id: dm_truong_id,
                hoc_ky: hoc_ky,
                nam_hoc: nam_hoc,
                tuan_thu: 0
            });

            if (!res.is_success) {
                NotifyHelper.Error(res.message ?? "Lấy TKB thất bại");
                return;
            }

            const tkb = res.data;
            const slotDurationMin = scheduleConfig.duration || 15;

            let countBusy = 0;

            setMeeting(prev => {
                const updatedSlots = (prev.slots ?? []).map(slot => {
                    const slotStart = new Date(slot.start_at);
                    const slotEnd = new Date(slotStart.getTime() + slotDurationMin * 60000);

                    const isBusy = tkb.some((t: pl_tkb_chitiet_view) => {
                        if (!t.ngay_hoc) return false;

                        const start = new Date(t.ngay_hoc);

                        const tkbStart = new Date(start);
                        tkbStart.setHours(
                            parseInt(t.gio_bat_dau.split(":")[0]),
                            parseInt(t.gio_bat_dau.split(":")[1]),
                            0
                        );

                        const tkbEnd = new Date(start);
                        tkbEnd.setHours(
                            parseInt(t.gio_ket_thuc.split(":")[0]),
                            parseInt(t.gio_ket_thuc.split(":")[1]),
                            0
                        );

                        return (
                            t.sis_giaovien_id === slot.sis_giaovien_id &&
                            slotStart < tkbEnd &&
                            slotEnd > tkbStart
                        );
                    });

                    if (isBusy && slot.status !== SlotStatus.Busy) {
                        countBusy++;
                        return {
                            ...slot,
                            status: SlotStatus.Busy
                        };
                    }

                    return slot;
                });

                return {
                    ...prev,
                    slots: updatedSlots
                };
            });

            NotifyHelper.Success(`Đã cập nhật ${countBusy} khung giờ bận từ TKB`);

        } catch (err) {
            NotifyHelper.Error("Có lỗi xảy ra khi import lịch bận");
        }
    };

    const handleAddBusy = async () => {
        try {
            const res = await meetingBusyApi.select(nam_hoc);

            if (!res.is_success) {
                NotifyHelper.Error(res.message ?? "Lấy lịch bận thất bại");
                return;
            }

            const teacherIds = new Set([
                homeroomTeacherId,
                ...selectedSubjects.map(x => x.sis_giaovien_id)
            ]);

            const busyList: IMeetingBusy[] = (res.data as IMeetingBusy[]).filter(b =>
                teacherIds.has(b.sis_giaovien_id)
            );

            let countBusy = 0;

            const slotDurationMin = scheduleConfig.duration || 15;

            setMeeting(prev => {
                const updatedSlots = (prev.slots ?? []).map(slot => {
                    const slotStart = new Date(slot.start_at);
                    const slotEnd = new Date(slotStart.getTime() + slotDurationMin * 60000);

                    const isBusy = busyList.some(b => {
                        if (!b.start_time || !b.end_time) return false;
                        if (b.sis_giaovien_id !== slot.sis_giaovien_id) return false;

                        const busyStart = new Date(b.start_time);
                        const busyEnd = new Date(b.end_time);

                        return slotStart < busyEnd && slotEnd > busyStart;
                    });

                    if (isBusy && slot.status !== SlotStatus.Busy) {
                        countBusy++;
                        return { ...slot, status: SlotStatus.Busy };
                    }

                    return slot;
                });

                return { ...prev, slots: updatedSlots };
            });

            NotifyHelper.Success(`Đã cập nhật ${countBusy} khung giờ bận từ lịch giáo viên`);

        } catch (err) {
            NotifyHelper.Error("Có lỗi xảy ra khi thêm lịch bận");
        }
    };

    const handleImportPicksToAssigns = async () => {
        try {
            const res = await meetingPickApi.select(nam_hoc, hoc_ky);

            if (!res.is_success) {
                NotifyHelper.Error(res.message ?? "Lấy danh sách chọn thất bại");
                return;
            }

            const picks: IMeetingPick[] = res.data ?? [];
            const hocSinhIdSet = new Set(hocSinhs.map((hs: any) => hs.id));

            let countAdded = 0;

            setMeeting(prev => {
                const newAssigns = [...(prev.assigns ?? [])];

                picks.forEach(pick => {
                    if (!hocSinhIdSet.has(pick.ts_hocsinh_id)) return;

                    const addIfNotExists = (subjectId: number, giaoVienId: number) => {
                        const exists = newAssigns.some(a =>
                            a.ts_hocsinh_id === pick.ts_hocsinh_id &&
                            a.sis_lop_monhoc_id === subjectId
                        );
                        if (!exists) {
                            countAdded++;
                            newAssigns.push({
                                id: 0,
                                sis_meeting_id: prev.id,
                                sis_giaovien_id: giaoVienId,
                                sis_lop_monhoc_id: subjectId,
                                ts_hocsinh_id: pick.ts_hocsinh_id
                            });
                        }
                    };

                    if (pick.sis_giaovien_id === homeroomTeacherId) {
                        addIfNotExists(0, homeroomTeacherId);
                    }

                    selectedSubjects
                        .filter(s => s.sis_giaovien_id === pick.sis_giaovien_id)
                        .forEach(s => addIfNotExists(s.id, s.sis_giaovien_id));
                });

                return { ...prev, assigns: newAssigns };
            });

            NotifyHelper.Success(`Đã thêm ${countAdded} chỉ định từ danh sách GV chọn`);

        } catch (err) {
            NotifyHelper.Error("Có lỗi xảy ra khi import danh sách chọn");
        }
    };

    const isAssigned = (hsId: number, subjectId: number) => {
        return meeting.assigns?.some(a =>
            a.ts_hocsinh_id === hsId &&
            a.sis_lop_monhoc_id === subjectId
        );
    };

    const toggleAssign = (hs: any, subject: Subject) => {
        setMeeting(prev => {
            const exists = prev.assigns?.find(a =>
                a.ts_hocsinh_id === hs.id &&
                a.sis_lop_monhoc_id === subject.id
            );

            let newAssigns = [...(prev.assigns ?? [])];

            if (exists) {
                newAssigns = newAssigns.filter(a =>
                    !(a.ts_hocsinh_id === hs.id && a.sis_lop_monhoc_id === subject.id)
                );
            } else {
                newAssigns.push({
                    id: 0,
                    sis_meeting_id: prev.id,
                    sis_giaovien_id: subject.sis_giaovien_id,
                    sis_lop_monhoc_id: subject.id,
                    ts_hocsinh_id: hs.id
                });
            }

            return {
                ...prev,
                assigns: newAssigns
            };
        });
    };

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#F5F8FA",
                    borderBottomColor: "border.default",
                    borderBottomStyle: "solid",
                    p: 2,
                    borderBottomWidth: "1px"
                }}>
                    <Button text="Back" size="medium" variant="invisible" leadingVisual={ArrowLeftIcon}
                        onClick={() => {
                            navigate(-1);
                        }}
                    />
                    <Box sx={{
                        flex: 1,
                        textAlign: "center",
                        whiteSpace: "break-spaces",
                        fontWeight: 600,
                        fontSize: "16px"
                    }}>
                        {meeting?.name ?? "Unknow"}
                    </Box>
                    <Button text={meeting?.id > 0 ? 'Cập nhật' : 'Lưu'} variant="primary" sx={{ ml: 2 }} size="medium" type="submit" />
                </Box>
                <Box sx={{
                    p: 3,
                    display: "flex",
                    overflowY: "auto",
                    height: window.innerHeight - 150,
                }}>

                    <Box id="center" sx={{
                        flex: 1,
                        display: "flex"
                    }}>
                        <Box
                            display={"grid"}
                            sx={{
                                gap: 2,
                                flex: 1,
                                pl: 3
                            }}
                        >
                            <Box
                                display={"grid"}
                                sx={{
                                    gap: 2
                                }}
                            >
                                <Box id="genaral" sx={{ mb: 3, maxWidth: '700px' }}>
                                    <Box sx={{
                                        borderBottom: "1px solid",
                                        borderBottomColor: "border.default",
                                        mb: 3,
                                        pb: 1
                                    }}>
                                        <Text text="Thông tin cơ bản" sx={{ fontSize: 24, fontWeight: "600" }} />
                                    </Box>

                                    <Box sx={{ display: "grid", gap: 16 }}>

                                        {/* ===== TÊN ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Tên cuộc họp</FormControl.Label>
                                                <TextInput
                                                    register={register}
                                                    required
                                                    validateMessage='Vui lòng điền Tên cuộc họp'
                                                    name="name"
                                                    errors={errors}
                                                    width={'100%'}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Tên cuộc họp (tiếng anh)</FormControl.Label>
                                                <TextInput
                                                    register={register}
                                                    required
                                                    validateMessage='Vui lòng điền Tên cuộc họp (Tiếng anh)'
                                                    name="name_en"
                                                    errors={errors}
                                                    width={'100%'}
                                                />
                                            </FormControl>
                                        </Box>

                                        {/* ===== LỚP + NGÀY ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Lớp</FormControl.Label>
                                                <ComboboxLop
                                                    dm_truong_id={dm_truong_id}
                                                    nam_hoc={nam_hoc}
                                                    value={lopIdsSelected.length > 0 ? lopIdsSelected[0] : 0}
                                                    isAddFromLopBoMon={dm_truong_id === 31}
                                                    onValueChanged={(lopId, data) => {
                                                        setMeeting(prev => ({
                                                            ...prev,
                                                            scopes: [{
                                                                id: 0,
                                                                sis_meeting_id: prev.id,
                                                                dm_lop_id: lopId
                                                            }]
                                                        }));

                                                        setHomeroomTeacherId(data?.sis_giaovien_id_chunhiem || 0);
                                                        setHomeroomTeacherName(data?.sis_giaovien_ho_ten_chunhiem || "");
                                                        clearErrors("scopes");
                                                    }}
                                                    width={'100%'}
                                                />
                                            </FormControl>

                                            <Controller
                                                control={control}
                                                name="event_time_start"
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <FormControl.Label>Diễn ra từ</FormControl.Label>
                                                        <DateInput
                                                            value={field.value ? moment(field.value).format("DD/MM/YYYY") : undefined}
                                                            onValueChanged={(text, date) => {
                                                                if (date) {
                                                                    field.onChange(moment(date).format("YYYY-MM-DD"));
                                                                    setMeeting(prev => ({
                                                                        ...prev,
                                                                        event_time_start: moment(date).format("YYYY-MM-DD")
                                                                    }));
                                                                }
                                                            }}
                                                            width={'100%'}
                                                        />
                                                    </FormControl>
                                                )}
                                            />

                                            <Controller
                                                control={control}
                                                name="event_time_end"
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <FormControl.Label>Đến</FormControl.Label>
                                                        <DateInput
                                                            value={field.value ? moment(field.value).format("DD/MM/YYYY") : undefined}
                                                            onValueChanged={(text, date) => {
                                                                if (date) {
                                                                    field.onChange(moment(date).format("YYYY-MM-DD"));
                                                                    setMeeting(prev => ({
                                                                        ...prev,
                                                                        event_time_end: moment(date).format("YYYY-MM-DD")
                                                                    }));
                                                                }
                                                            }}
                                                            width={'100%'}
                                                        />
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* ===== GIỜ ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Buổi sáng từ</FormControl.Label>
                                                <TextInput
                                                    type="time"
                                                    value={scheduleConfig.morningStart}
                                                    onChange={(e) =>
                                                        setScheduleConfig({ ...scheduleConfig, morningStart: e.target.value })
                                                    }
                                                    width={'100%'}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Đến</FormControl.Label>
                                                <TextInput
                                                    type="time"
                                                    value={scheduleConfig.morningEnd}
                                                    onChange={(e) =>
                                                        setScheduleConfig({ ...scheduleConfig, morningEnd: e.target.value })
                                                    }
                                                    width={'100%'}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Buổi chiều từ</FormControl.Label>
                                                <TextInput
                                                    type="time"
                                                    value={scheduleConfig.afternoonStart}
                                                    onChange={(e) =>
                                                        setScheduleConfig({ ...scheduleConfig, afternoonStart: e.target.value })
                                                    }
                                                    width={'100%'}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Đến</FormControl.Label>
                                                <TextInput
                                                    type="time"
                                                    value={scheduleConfig.afternoonEnd}
                                                    onChange={(e) =>
                                                        setScheduleConfig({ ...scheduleConfig, afternoonEnd: e.target.value })
                                                    }
                                                    width={'100%'}
                                                />
                                            </FormControl>
                                        </Box>

                                        {/* ===== CONFIG ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Thời lượng nghỉ mỗi khung giờ</FormControl.Label>
                                                <TextInput
                                                    type="number"
                                                    value={scheduleConfig.breakTime}
                                                    onChange={(e) =>
                                                        setScheduleConfig({
                                                            ...scheduleConfig,
                                                            breakTime: Number(e.target.value)
                                                        })
                                                    }
                                                    width={'100%'}

                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Thời lượng mỗi khung giờ (phút)</FormControl.Label>
                                                <TextInput
                                                    type="number"
                                                    value={scheduleConfig.duration}
                                                    onChange={(e) =>
                                                        setScheduleConfig({
                                                            ...scheduleConfig,
                                                            duration: Number(e.target.value)
                                                        })
                                                    }
                                                    width={'100%'}

                                                />
                                            </FormControl>
                                        </Box>

                                        {/* ===== CHECKBOX ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Họp cùng GVBM</FormControl.Label>
                                                <FormControl.Caption>
                                                    GVCN sẽ họp cùng với GVBM
                                                </FormControl.Caption>
                                                <Checkbox
                                                    checked={meeting?.is_homeroom_subject_meeting ?? false}
                                                    onChange={(e) =>
                                                        setMeeting({
                                                            ...meeting,
                                                            is_homeroom_subject_meeting: e.target.checked
                                                        })
                                                    }
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <FormControl.Label>Chỉ định GVBM</FormControl.Label>
                                                <FormControl.Caption>
                                                    Chỉ định học sinh sẽ họp với GVBM nào
                                                </FormControl.Caption>
                                                <Checkbox
                                                    checked={meeting?.is_assign_teacher_enabled ?? false}
                                                    onChange={(e) =>
                                                        setMeeting({
                                                            ...meeting,
                                                            is_assign_teacher_enabled: e.target.checked
                                                        })
                                                    }
                                                />
                                            </FormControl>
                                        </Box>

                                        {/* ===== ĐĂNG KÝ ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Đăng ký từ ngày</FormControl.Label>
                                                <TextInput
                                                    name="registration_start_at"
                                                    type="datetime-local"
                                                    register={register}
                                                    required
                                                    block
                                                    validateMessage='Vui lòng điền thời gian bắt đầu đăng ký'
                                                    errors={errors}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Đến ngày</FormControl.Label>
                                                <TextInput
                                                    name="registration_end_at"
                                                    type="datetime-local"
                                                    register={register}
                                                    required
                                                    block
                                                    validateMessage='Vui lòng điền thời gian kết thúc đăng ký'
                                                    errors={errors}
                                                />
                                            </FormControl>
                                        </Box>

                                        {/* ===== PUBLISH ===== */}
                                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                            <FormControl>
                                                <FormControl.Label>Publish</FormControl.Label>
                                                <FormControl.Caption>
                                                    Sau khi Published, PHHS có thể nhìn thấy và đăng ký Meeting
                                                </FormControl.Caption>
                                                <Checkbox
                                                    checked={meeting?.is_published ?? false}
                                                    onChange={(e) =>
                                                        setMeeting({
                                                            ...meeting,
                                                            is_published: e.target.checked
                                                        })
                                                    }
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormControl.Label>Cho phép đăng ký lại</FormControl.Label>
                                                <FormControl.Caption>
                                                    PHHS có thể sửa lại bản đăng ký
                                                </FormControl.Caption>
                                                <Checkbox
                                                    checked={meeting?.is_allow_re_submit ?? false}
                                                    onChange={(e) =>
                                                        setMeeting({
                                                            ...meeting,
                                                            is_allow_re_submit: e.target.checked
                                                        })
                                                    }
                                                />
                                            </FormControl>
                                        </Box>

                                    </Box>
                                </Box>
                                <Box id="schedule" sx={{ mb: 3, overflow: "auto" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            borderBottom: "1px solid",
                                            borderBottomColor: "border.default",
                                            mb: 3,
                                            pb: 1
                                        }}
                                    >
                                        <Text text="Khung giờ họp" sx={{ fontSize: 24, fontWeight: "600" }} />
                                        <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
                                            <Button
                                                text="Thêm lịch bận từ TKB"
                                                variant="default"
                                                size="small"
                                                leadingVisual={SyncIcon}
                                                onClick={handleImportBusyFromTkb}
                                            />
                                            <Button
                                                text="Thêm lịch bận của GV nhập"
                                                variant="default"
                                                size="small"
                                                leadingVisual={SyncIcon}
                                                onClick={handleAddBusy}
                                            />
                                            <Button text="Thêm lớp bộ môn" variant="primary" size="small" leadingVisual={PlusIcon} onClick={() => {
                                                setOpenSubjectModal(true);
                                            }} />
                                        </Box>
                                    </Box>

                                    {meeting?.event_time_start && (
                                        <MeetingSchedule
                                            meeting={meeting}
                                            setMeeting={setMeeting}
                                            errors={errors}
                                            isGroupByTeacher
                                        />
                                    )}
                                </Box>
                                {hocSinhs.length > 0 && selectedSubjects.length > 0 && meeting.is_assign_teacher_enabled &&
                                    <>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                borderBottom: "1px solid",
                                                borderBottomColor: "border.default",
                                                mb: 3,
                                                pb: 1
                                            }}
                                        >
                                            <Text text="Chỉ định GVBM" sx={{ fontSize: 24, fontWeight: "600" }} />
                                            <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
                                                <Button
                                                    text="Thêm từ danh sách GV chọn"
                                                    variant="default"
                                                    size="small"
                                                    leadingVisual={SyncIcon}
                                                    onClick={handleImportPicksToAssigns}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{ overflow: 'auto' }}>
                                            <AssignTeacherGrid
                                                hocSinhs={hocSinhs}
                                                subjects={selectedSubjects}
                                                assigns={meeting.assigns ?? []}
                                                setAssigns={(fn) =>
                                                    setMeeting(prev => ({
                                                        ...prev,
                                                        assigns: fn(prev.assigns ?? [])
                                                    }))
                                                }
                                            />
                                        </Box>
                                    </>
                                }

                            </Box>
                        </Box>
                    </Box>
                </Box>
                {openSubjectModal && (
                    <SelectSubjectModal
                        subjects={subjectOptions}
                        lopId={lopIdsSelected[0]}
                        onClose={() => setOpenSubjectModal(false)}
                        selectedSubjects={selectedSubjects}
                        onSubmit={(s) => {
                            if (!s) return;

                            if (selectedSubjects.some(x => x.id === s.id)) {
                                setOpenSubjectModal(false);
                                return;
                            }

                            setSelectedSubjects(prev => [
                                ...prev,
                                {
                                    id: s.id,
                                    name: s.name,
                                    sis_giaovien_id: s.sis_giaovien_id
                                }
                            ]);

                            setOpenSubjectModal(false);
                        }}
                    />
                )}
            </form>
        </Box>
    );
};

export default MeetingDetail;